import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Sun, Moon, Eye, EyeOff, RefreshCw, LogOut, Crown, Settings, Share2, MoreHorizontal } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { IconButton } from '../shared/Button';
import { StreakBadge } from '../streak/StreakBadge';
import { formatCurrency } from '../../utils/formatters';

interface HeaderProps {
  onRefresh: () => void;
  onExportPDF?: () => Promise<void>;
  isRefreshing?: boolean;
  onUpgradeClick?: () => void;
  onSettingsClick?: () => void;
  hasNewUpdates?: boolean;
}

interface MenuItemProps {
  darkMode: boolean;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  close: () => void;
  disabled?: boolean;
  danger?: boolean;
  badge?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({ darkMode, icon, label, onClick, close, disabled, danger, badge }) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled || !onClick}
    onClick={() => {
      close();
      onClick?.();
    }}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-left transition-colors disabled:cursor-default ${
      danger
        ? darkMode ? 'text-red-300 hover:bg-red-500/15' : 'text-red-600 hover:bg-red-50'
        : darkMode ? 'text-slate-200 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'
    } ${disabled ? 'opacity-60' : ''}`}
  >
    <span className="flex-shrink-0">{icon}</span>
    <span className="flex-1">{label}</span>
    {badge && <span className="h-2 w-2 rounded-full bg-red-500" />}
  </button>
);

export const Header: React.FC<HeaderProps> = ({
  onRefresh,
  onExportPDF,
  isRefreshing = false,
  onUpgradeClick,
  onSettingsClick,
  hasNewUpdates = false,
}) => {
  const { darkMode, privacyMode, toggleDarkMode, togglePrivacyMode } = useTheme();
  const { paymentTier, handleSignOut, toggleDevTier } = useAuth();
  const { netWorth, undoStack, handleUndo } = useFinancialData();

  const isPro = paymentTier === 'pro';
  const [showLogoTrace, setShowLogoTrace] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 8 });
  const menuRef = useRef<HTMLDivElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || menuPanelRef.current?.contains(target)) return;
      closeMenu();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeMenu();
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    const timer = setTimeout(() => setShowLogoTrace(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleShare = async () => {
    const shareData = {
      title: 'TallyUp Finance',
      text: 'Track your net worth, investments, and budget all in one place.',
      url: 'https://www.tallyup.finance',
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
      }
    } catch {
      // User cancelled or share failed silently
    }
  };

  return (
    <div className={`px-2 py-3 md:px-3 md:py-4 ${darkMode ? 'backdrop-blur-xl bg-slate-950/80 border-b border-slate-800' : 'backdrop-blur-xl bg-white/80 border-b border-slate-200'} shadow-[0_32px_64px_rgba(107,56,212,0.06)]`}>
      {showLogoTrace && (
        <style>{`
          @property --trace-angle {
            syntax: '<angle>';
            initial-value: 0deg;
            inherits: false;
          }
          @keyframes border-trace {
            to { --trace-angle: 360deg; }
          }
          @keyframes border-fade {
            to { opacity: 0; }
          }
          .logo-trace::before {
            content: '';
            position: absolute;
            inset: -2px;
            border-radius: 14px;
            padding: 2px;
            background: conic-gradient(from 0deg, #8B5CF6 var(--trace-angle), transparent var(--trace-angle));
            -webkit-mask:
              linear-gradient(#fff 0 0) content-box,
              linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            animation: border-trace 0.8s ease-out forwards, border-fade 0.3s ease-out 0.9s forwards;
            pointer-events: none;
          }
        `}</style>
      )}
      <>
        {/* Desktop Header */}
        <div className="hidden lg:flex justify-between items-center gap-4 min-w-0">
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <div className={`relative rounded-xl p-1.5 bg-gradient-to-b from-white/90 to-white border border-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.12),inset_0_1px_2px_rgba(255,255,255,0.9),inset_0_-1px_3px_rgba(0,0,0,0.06)] backdrop-blur-xl ${showLogoTrace ? 'logo-trace' : ''}`}>
              <img
                src="/logo.png"
                alt="TallyUp Finance"
                className="h-10 w-10 md:h-11 md:w-11"
              />
            </div>
            <div>
              <h1 className={`text-3xl md:text-4xl font-bold leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                TallyUp
              </h1>
              <p className={`text-sm md:text-base font-medium tracking-[0.2em] pl-1 ${darkMode ? 'text-violet-300' : 'text-violet-600'}`}>
                Finance
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 justify-end min-w-0 flex-shrink">
            {/* Net Worth Display */}
            <div
              data-tour="net-worth-display"
              className={`px-4 py-2 rounded-xl backdrop-blur-md ${
                darkMode
                  ? 'bg-gradient-to-br from-violet-500/20 to-violet-400/10 border border-violet-500/30'
                  : 'bg-gradient-to-br from-violet-500/10 to-violet-400/5 border border-violet-300/40'
              } flex items-center gap-2 whitespace-nowrap`}
            >
              <span className={`text-sm font-semibold leading-none hidden xl:inline ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>
                Net Worth
              </span>
              <span className={`text-sm font-bold leading-none ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                {formatCurrency(netWorth, privacyMode)}
              </span>
            </div>

            <StreakBadge />

            {/* Actions */}
            <div className="flex gap-2">
              {/* PDF Export - Pro Only */}
              {isPro && onExportPDF && (
                <button
                  onClick={onExportPDF}
                  className={`px-3 py-2 rounded-xl backdrop-blur-md ${
                    darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'
                  } transition-all ${darkMode ? 'text-white' : 'text-slate-800'} text-sm font-semibold flex items-center gap-2 whitespace-nowrap`}
                  title="Export PDF Report"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  <span className="hidden xl:inline">Export PDF</span>
                </button>
              )}

              {/* Tier Badge / Upgrade */}
              {toggleDevTier ? (
                <button
                  onClick={toggleDevTier}
                  className={`px-3 py-2 rounded-xl backdrop-blur-md ${
                    darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'
                  } transition-all ${darkMode ? 'text-white' : 'text-slate-800'} text-sm font-semibold flex items-center gap-2 ${
                    !isPro ? 'border border-dashed border-yellow-500/60' : ''
                  }`}
                  title="Toggle tier (Dev)"
                >
                  <Crown size={16} className={isPro ? 'text-yellow-500' : 'text-slate-400'} />
                  <span className="hidden xl:inline">{isPro ? 'Pro' : 'Free'}</span>
                  <span className="hidden xl:inline text-[10px] opacity-60">DEV</span>
                </button>
              ) : isPro ? (
                <div
                  className={`px-3 py-2 rounded-xl backdrop-blur-md ${
                    darkMode ? 'bg-white/10' : 'bg-slate-100'
                  } ${darkMode ? 'text-white' : 'text-slate-800'} text-sm font-semibold flex items-center gap-2`}
                >
                  <Crown size={16} className="text-yellow-500" />
                  <span className="hidden xl:inline">Pro</span>
                </div>
              ) : (
                <button
                  onClick={onUpgradeClick}
                  className={`px-3 py-2 rounded-xl backdrop-blur-md ${
                    darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'
                  } transition-all ${darkMode ? 'text-white' : 'text-slate-800'} text-sm font-semibold flex items-center gap-2`}
                  title="Upgrade to Pro"
                >
                  <Crown size={16} className="text-slate-400" />
                  <span className="hidden xl:inline">Free</span>
                </button>
              )}

              {/* Undo */}
              <IconButton
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 7v6h6" />
                    <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
                  </svg>
                }
                variant="secondary"
                onClick={handleUndo}
                disabled={undoStack.length === 0}
                tooltip={undoStack.length > 0 ? 'Undo last change' : 'No changes to undo'}
              />

              {/* Refresh */}
              <div data-tour="refresh-button">
                <IconButton
                  icon={<RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />}
                  variant="secondary"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  tooltip="Refresh data"
                />
              </div>

              {/* Privacy Mode */}
              <div data-tour="privacy-toggle">
                <IconButton
                  icon={privacyMode ? <EyeOff size={20} /> : <Eye size={20} />}
                  variant="secondary"
                  onClick={togglePrivacyMode}
                  tooltip="Toggle privacy mode"
                />
              </div>

              {/* Dark Mode */}
              <IconButton
                icon={darkMode ? <Sun size={20} /> : <Moon size={20} />}
                variant="secondary"
                onClick={toggleDarkMode}
              />

              {/* Share */}
              <IconButton
                icon={<Share2 size={20} />}
                variant="secondary"
                onClick={handleShare}
                tooltip="Share"
              />

              {/* Settings */}
              <div data-tour="settings-button" className="relative">
                <IconButton
                  icon={<Settings size={20} />}
                  variant="secondary"
                  onClick={onSettingsClick}
                  tooltip="Settings"
                />
                {hasNewUpdates && (
                  <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-slate-900 pointer-events-none" />
                )}
              </div>

              {/* Sign Out */}
              <IconButton
                icon={<LogOut size={20} />}
                variant="danger"
                onClick={handleSignOut}
                tooltip="Sign out"
              />
            </div>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="lg:hidden">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className={`relative rounded-xl p-1 shadow-lg backdrop-blur-xl bg-white/90 border border-white/50 flex-shrink-0 ${showLogoTrace ? 'logo-trace' : ''}`}>
                <img
                  src="/logo.png"
                  alt="TallyUp Finance"
                  className="h-8 w-8 flex-shrink-0"
                />
              </div>
              <div>
                <h1 className={`text-xl font-bold leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  TallyUp
                </h1>
                <p className={`text-[9px] font-medium tracking-[0.15em] pl-0.5 ${darkMode ? 'text-violet-300' : 'text-violet-600'}`}>
                  Finance
                </p>
              </div>
            </div>
            <div className="flex gap-1 items-center">
              <StreakBadge compact />
              <IconButton
                icon={privacyMode ? <EyeOff size={18} /> : <Eye size={18} />}
                variant="secondary"
                onClick={togglePrivacyMode}
              />
              <div className="hidden sm:flex gap-1 items-center">
                <IconButton
                  icon={<RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />}
                  variant="secondary"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  tooltip="Refresh data"
                />
                <IconButton
                  icon={darkMode ? <Sun size={18} /> : <Moon size={18} />}
                  variant="secondary"
                  onClick={toggleDarkMode}
                  tooltip={darkMode ? 'Light mode' : 'Dark mode'}
                />
                <IconButton
                  icon={<Crown size={18} className={isPro ? 'text-yellow-500' : undefined} />}
                  variant="secondary"
                  onClick={toggleDevTier || (!isPro ? onUpgradeClick : undefined)}
                  tooltip={isPro ? 'Pro' : 'Upgrade to Pro'}
                />
                <IconButton
                  icon={<Share2 size={18} />}
                  variant="secondary"
                  onClick={handleShare}
                  tooltip="Share"
                />
                <div className="relative">
                  <IconButton
                    icon={<Settings size={18} />}
                    variant="secondary"
                    onClick={onSettingsClick}
                    tooltip="Settings"
                  />
                  {hasNewUpdates && (
                    <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-slate-900 pointer-events-none" />
                  )}
                </div>
                <IconButton
                  icon={<LogOut size={18} />}
                  variant="danger"
                  onClick={handleSignOut}
                  tooltip="Sign out"
                />
              </div>
              <div ref={menuRef} className="relative sm:hidden">
                <IconButton
                  icon={<MoreHorizontal size={18} />}
                  variant="secondary"
                  onClick={() => {
                    const rect = menuRef.current?.getBoundingClientRect();
                    if (rect) setMenuPos({ top: rect.bottom + 8, right: Math.max(window.innerWidth - rect.right, 8) });
                    setMenuOpen(o => !o);
                  }}
                  aria-label="More options"
                  aria-expanded={menuOpen}
                />
                {hasNewUpdates && (
                  <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-slate-900 pointer-events-none" />
                )}
                {menuOpen && createPortal(
                  <div
                    ref={menuPanelRef}
                    role="menu"
                    style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
                    className={`z-50 w-56 rounded-2xl p-1.5 shadow-2xl border ${
                      darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'
                    }`}
                  >
                    <MenuItem
                      darkMode={darkMode}
                      icon={<RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />}
                      label={isRefreshing ? 'Refreshing…' : 'Refresh'}
                      onClick={onRefresh}
                      disabled={isRefreshing}
                      close={closeMenu}
                    />
                    <MenuItem
                      darkMode={darkMode}
                      icon={darkMode ? <Sun size={18} /> : <Moon size={18} />}
                      label={darkMode ? 'Light mode' : 'Dark mode'}
                      onClick={toggleDarkMode}
                      close={closeMenu}
                    />
                    <MenuItem
                      darkMode={darkMode}
                      icon={<Crown size={18} className={isPro ? 'text-yellow-500' : undefined} />}
                      label={isPro ? 'Pro' : 'Upgrade to Pro'}
                      onClick={toggleDevTier || (!isPro ? onUpgradeClick : undefined)}
                      close={closeMenu}
                    />
                    <MenuItem
                      darkMode={darkMode}
                      icon={<Share2 size={18} />}
                      label="Share TallyUp"
                      onClick={handleShare}
                      close={closeMenu}
                    />
                    <MenuItem
                      darkMode={darkMode}
                      icon={<Settings size={18} />}
                      label="Settings"
                      onClick={onSettingsClick}
                      close={closeMenu}
                      badge={hasNewUpdates}
                    />
                    <div className={`my-1 border-t ${darkMode ? 'border-slate-800' : 'border-slate-100'}`} />
                    <MenuItem
                      darkMode={darkMode}
                      icon={<LogOut size={18} />}
                      label="Sign out"
                      onClick={handleSignOut}
                      close={closeMenu}
                      danger
                    />
                  </div>,
                  document.body
                )}
              </div>
            </div>
          </div>

        </div>
      </>
    </div>
  );
};
